/**
 * The one error vocabulary for adding a custom background, spanning both halves of the operation:
 * decoding/re-encoding the picked file (`normaliseBackgroundImage`) and getting it into localStorage
 * (`customBackgroundStorage`).
 *
 * Deliberately one flat union rather than two, so the picker has exactly one `t(`errors.${code}`)`
 * mapping and never has to care which half failed. Shaped as a `code`-carrying `Error` plus a guard
 * rather than an Error subclass, matching `devices/utils/isAgoraRTCError`.
 */
export type BackgroundImageErrorCode =
  | 'unsupportedType'
  | 'tooLarge'
  | 'decodeFailed'
  | 'encodeFailed'
  | 'limitReached'
  | 'quotaExceeded'
  | 'storageFailed'
  /** Should be unreachable through the UI — the picker hides the entry point. See the thunk. */
  | 'featureDisabled';

export type BackgroundImageError = Error & { code: BackgroundImageErrorCode };

export const createBackgroundImageError = (code: BackgroundImageErrorCode, message?: string): BackgroundImageError =>
  Object.assign(new Error(message ?? code), { code });

export const isBackgroundImageError = (error: unknown): error is BackgroundImageError =>
  error instanceof Error && typeof (error as Partial<BackgroundImageError>).code === 'string';
